/**
 * sessionCap.ts — Pure, side-effect-free session cap planner.
 *
 * Used by http.ts init branch to decide which sessions to evict before
 * admitting a new one for the presenting API key.
 *
 * Two eviction sites in the codebase:
 *   1. planKeyAdmission (here) — per-key, on init, targets only the presenting key.
 *   2. evictStaleSessions (http.ts) — global TTL sweep, runs every 60 s.
 * Keep both coherent; a change to one may affect the other.
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface CapSession {
  /** Unix-ms timestamp of last request start (or completion). */
  lastAccess: number;
  /** Hash of the API key that owns this session. */
  keyHash: string;
  /**
   * Number of POST tool-call requests currently being handled.
   * Incremented at POST start; decremented in finally.
   * GET SSE handlers and DELETE are NOT counted here — see design §3.1.
   */
  inFlight: number;
}

export interface AdmissionPlan {
  /** IDs of sessions the caller should evict before admitting the new one. */
  evict: string[];
  /** True if the new session can be admitted after evictions. */
  admit: boolean;
  /**
   * Seconds the caller should suggest as a retry delay when admit=false.
   * 0 when admit=true.
   */
  retryAfterSeconds: number;
  /**
   * True when admit=false AND every blocking same-key session has inFlight > 0
   * — there is no grace deadline to wait out, so retryAfterSeconds is a fixed
   * poll-hint rather than a guarantee. The caller surfaces this as a "retry
   * shortly" message instead of a hard deadline. Always false when admit=true.
   * This is the single source of truth for poll-vs-deadline — callers must not
   * re-derive it from the live session map (avoids a second clock read).
   */
  retryIsPoll: boolean;
}

// ---------------------------------------------------------------------------
// planKeyAdmission
// ---------------------------------------------------------------------------

/**
 * Plan how the presenting key admits ONE new session under `cap`.
 *
 * Protected = inFlight > 0 OR (now - lastAccess) < graceMs. Never evicted.
 *
 * 1. Throw RangeError if cap < 1.
 * 2. Collect entries belonging to `keyHash`.
 * 3. Mark the key's TTL-expired-AND-not-protected sessions for eviction (G2).
 *    (inFlight===0 && now-lastAccess >= ttlMs)
 * 4. If survivors (key ids not in evict) >= cap, additionally evict the key's
 *    idle sessions (inFlight===0 && now-lastAccess >= graceMs) sorted
 *    ascending by lastAccess (Map-insertion-order stable sort resolves ties)
 *    until survivors < cap or none remain.
 * 5. admit = survivors.length < cap.
 * 6. retryAfterSeconds: 0 when admit; else computed from the oldest
 *    grace-blocker (smallest lastAccess), or 5 if every blocker is inFlight
 *    (retryIsPoll=true in that all-inFlight case).
 * 7. Return { evict, admit, retryAfterSeconds, retryIsPoll }.
 *
 * Only the presenting key's sessions are ever touched.
 * Pure; total over empty maps. Ties in lastAccess resolve by Map insertion order.
 */
export function planKeyAdmission(
  sessions: ReadonlyMap<string, CapSession>,
  keyHash: string,
  cap: number,
  now: number,
  opts: { ttlMs: number; graceMs: number },
): AdmissionPlan {
  if (cap < 1) {
    throw new RangeError(`cap must be >= 1, got ${cap}`);
  }

  const { ttlMs, graceMs } = opts;

  // Helper: a session is "protected" if it has an active POST call OR was
  // recently accessed (within the grace window).
  const isProtected = (s: CapSession): boolean =>
    s.inFlight > 0 || now - s.lastAccess < graceMs;

  // Step 2: collect the presenting key's entries as an ordered array.
  // Map iteration order equals insertion order (ES2015+; V8 stable).
  const keyEntries: [string, CapSession][] = [];
  for (const [id, session] of sessions) {
    if (session.keyHash === keyHash) {
      keyEntries.push([id, session]);
    }
  }

  // Step 3: always evict TTL-expired AND non-protected sessions (G2).
  const evictSet = new Set<string>();
  for (const [id, session] of keyEntries) {
    if (!isProtected(session) && now - session.lastAccess >= ttlMs) {
      evictSet.add(id);
    }
  }

  // Step 4: if survivors (key sessions not in evict) >= cap, additionally
  // evict idle sessions (inFlight===0 && now-lastAccess >= graceMs) sorted
  // ascending by lastAccess until survivors < cap.
  let survivors = keyEntries.filter(([id]) => !evictSet.has(id));

  if (survivors.length >= cap) {
    // Collect idle (evictable) survivors, preserving insertion order for
    // stable sort — Array.sort is stable in V8 and spec-required ES2019+.
    const idleSurvivors = survivors
      .filter(([, s]) => !isProtected(s))
      // Ascending by lastAccess (oldest first); equal values keep insertion order.
      .sort(([, a], [, b]) => a.lastAccess - b.lastAccess);

    for (const [id] of idleSurvivors) {
      if (survivors.length < cap) break;
      evictSet.add(id);
      // Recompute survivors in-place without the evicted id.
      survivors = survivors.filter(([sid]) => sid !== id);
    }
  }

  const evict = Array.from(evictSet);

  // Step 5.
  const admit = survivors.length < cap;

  // Step 6.
  let retryAfterSeconds = 0;
  let retryIsPoll = false;
  if (!admit) {
    // Grace-blockers: surviving sessions with inFlight===0 (blocked by grace only).
    const graceBlockers = survivors.filter(([, s]) => s.inFlight === 0);
    if (graceBlockers.length > 0) {
      // Target the oldest (smallest lastAccess) — crosses graceMs soonest.
      const minLastAccess = Math.min(...graceBlockers.map(([, s]) => s.lastAccess));
      const msRemaining = graceMs - (now - minLastAccess);
      retryAfterSeconds = Math.max(1, Math.ceil(msRemaining / 1000));
    } else {
      // All blockers have inFlight > 0; completion time is unpredictable. The
      // 5 s value is a poll-hint fallback, not a guarantee — flag it so the
      // caller's 429 reads as "retry shortly" rather than a hard deadline.
      retryAfterSeconds = 5;
      retryIsPoll = true;
    }
  }

  return { evict, admit, retryAfterSeconds, retryIsPoll };
}

// ---------------------------------------------------------------------------
// countKeySessions
// ---------------------------------------------------------------------------

/**
 * Count how many sessions in the map belong to `keyHash`.
 * Pure; returns 0 for an empty map.
 */
export function countKeySessions(
  sessions: ReadonlyMap<string, CapSession>,
  keyHash: string,
): number {
  let count = 0;
  for (const session of sessions.values()) {
    if (session.keyHash === keyHash) count++;
  }
  return count;
}
